import { freshModal } from "../../components/freshModal";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/reusableComponents/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "../../components/reusableComponents/Dialog";
import { Label } from "../../components/reusableComponents/Label";

function FreelanceFactureModalFormSession({ open, onOpenChange, onSubmit }) {
  const [formData, setFormData] = useState({
    numeroFacture: "",
    agencyName: "",
    montant: "",
    dateEcheance: "",
    dateEmission: "",
    description: "",
    fichierPDF: null,
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Effacer l'erreur quand l'utilisateur commence à taper
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setFormData(prev => ({ ...prev, fichierPDF: file }));
      setErrors(prev => ({ ...prev, fichierPDF: "" }));
    } else {
      toast.error("Veuillez sélectionner un fichier PDF valide");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.numeroFacture.trim()) {
      newErrors.numeroFacture = "Le numéro de facture est requis";
    }
    if (!formData.agencyName.trim()) {
      newErrors.agencyName = "Le nom de l'agence est requis";
    }
    if (!formData.montant || parseFloat(formData.montant) <= 0) {
      newErrors.montant = "Le montant doit être supérieur à 0";
    }
    if (!formData.dateEcheance) {
      newErrors.dateEcheance = "La date d'échéance est requise";
    }
    if (!formData.dateEmission) {
      newErrors.dateEmission = "La date d'émission est requise";
    }
    if (!formData.description.trim()) {
      newErrors.description = "La description est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs dans le formulaire");
      return;
    }

    const factureData = {
      numeroFacture: formData.numeroFacture,
      agencyName: formData.agencyName,
      montant: parseFloat(formData.montant),
      dateEcheance: formData.dateEcheance,
      dateEmission: formData.dateEmission,
      description: formData.description,
      fichierPDF: formData.fichierPDF,
      statut: "en attente", // Statut initial
      FactureRegle: "NON",
      createdAt: new Date().toISOString(),
    };

    onSubmit(factureData);
    
    // Réinitialiser le formulaire
    setFormData({
      numeroFacture: "",
      agencyName: "",
      montant: "",
      dateEcheance: "",
      dateEmission: "",
      description: "",
      fichierPDF: null,
    });
    setErrors({});
    onOpenChange(false);
  };

  const handleCancel = () => {
    setFormData({
      numeroFacture: "",
      agencyName: "",
      montant: "",
      dateEcheance: "",
      dateEmission: "",
      description: "",
      fichierPDF: null,
    });
    setErrors({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden bg-white rounded-lg shadow-lg">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-blue-700 to-indigo-800 py-6 px-8">
          <DialogTitle className="text-2xl font-bold text-white">
            Déposer une facture à l'agence
          </DialogTitle>
          <p className="text-blue-100 mt-1">
            Envoyez votre facture à l'agence pour traitement et paiement
          </p>
        </div>

        {/* Corps */}
        <div className="p-8 space-y-6 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="numeroFacture" className="text-sm font-medium text-gray-700">
                Numéro de facture *
              </Label>
              <input
                id="numeroFacture"
                type="text"
                value={formData.numeroFacture}
                onChange={(e) => handleInputChange("numeroFacture", e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.numeroFacture ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Ex: F2024-001"
              />
              {errors.numeroFacture && (
                <p className="mt-1 text-sm text-red-600">{errors.numeroFacture}</p>
              )}
            </div>

            <div>
              <Label htmlFor="agencyName" className="text-sm font-medium text-gray-700">
                Nom de l'agence *
              </Label>
              <input
                id="agencyName"
                type="text"
                value={formData.agencyName}
                onChange={(e) => handleInputChange("agencyName", e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.agencyName ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Ex: J'M Intérim Paris"
              />
              {errors.agencyName && (
                <p className="mt-1 text-sm text-red-600">{errors.agencyName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="montant" className="text-sm font-medium text-gray-700">
                Montant TTC (€) *
              </Label>
              <input
                id="montant"
                type="number"
                step="0.01"
                min="0"
                value={formData.montant}
                onChange={(e) => handleInputChange("montant", e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.montant ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="0.00"
              />
              {errors.montant && (
                <p className="mt-1 text-sm text-red-600">{errors.montant}</p>
              )}
            </div>

            <div>
              <Label htmlFor="dateEmission" className="text-sm font-medium text-gray-700">
                Date d'émission *
              </Label>
              <input
                id="dateEmission"
                type="date"
                value={formData.dateEmission}
                onChange={(e) => handleInputChange("dateEmission", e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.dateEmission ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.dateEmission && (
                <p className="mt-1 text-sm text-red-600">{errors.dateEmission}</p>
              )}
            </div>

            <div>
              <Label htmlFor="dateEcheance" className="text-sm font-medium text-gray-700">
                Date d'échéance *
              </Label>
              <input
                id="dateEcheance"
                type="date"
                value={formData.dateEcheance}
                onChange={(e) => handleInputChange("dateEcheance", e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.dateEcheance ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.dateEcheance && (
                <p className="mt-1 text-sm text-red-600">{errors.dateEcheance}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="description" className="text-sm font-medium text-gray-700">
              Description / Objet *
            </Label>
            <textarea
              id="description"
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.description ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Décrivez l'objet de la facture..."
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          <div>
            <Label htmlFor="fichierPDF" className="text-sm font-medium text-gray-700">
              Fichier PDF (optionnel)
            </Label>
            <input
              id="fichierPDF"
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              Vous pouvez joindre le PDF de votre facture
            </p>
            {formData.fichierPDF && (
              <p className="mt-1 text-sm text-green-600">
                Fichier sélectionné: {formData.fichierPDF.name}
              </p>
            )}
          </div>
        </div>

        {/* Pied de modal */}
        <DialogFooter className="flex justify-end px-8 py-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-gray-300 hover:bg-gray-100"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            className="ml-3 bg-blue-700 hover:bg-blue-800 text-white"
          >
            Déposer la facture
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const FreelanceFactureModal = freshModal(FreelanceFactureModalFormSession, "open");
